import type { PathLookupCheck } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { loadGraph } from 'components/Graph/commands/loadGraph';
import { setAppliedPreset } from 'components/Graph/commands/settings/setAppliedPreset';
import { mergeSettingsToPreset } from 'components/Graph/helpers/mergeSettingsToPreset';
import { useLocale } from 'containers/App/LocaleContext';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import { memo, useState } from 'react';
import type { FC } from 'react';
import { useConfirmDialog } from 'utils/hooks/useConfirmDialog';
import { GraphPathChecksFilter } from './GraphPathChecksFilter';
import { GraphPathChecksList } from './GraphPathChecksList';
import type { PathChecksHook } from './usePathChecks';

export const DELETE_CHECK_CONFIRM = 'deleteCheckConfirmDialog';

interface Props {
  pathChecksHook: PathChecksHook;
}

const BareGraphPathChecks: FC<Props> = (props) => {
  const msg = useLocale();
  const [deleteCheckId, setDeleteCheckId] = useState<string | null>(null);
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    presetDraft: prop('pathlookup', 'presetDraft'),
  });
  const deleteCheckConfirm = useConfirmDialog({ name: DELETE_CHECK_CONFIRM });

  const handleDeleteCheck = (checkId: string) => {
    setDeleteCheckId(checkId);
    deleteCheckConfirm.toggleConfirmDialog();
  };

  const handleDeleteCheckConfirm = async () => {
    if (deleteCheckId) {
      await props.pathChecksHook.deletePathLookupCheck({ id: deleteCheckId });
    }
  };

  const handleFetchGraph = async (item: PathLookupCheck) => {
    const preset = mergeSettingsToPreset(item.settings, ctxProps.presetDraft);

    await ctxProps.dispatch(setAppliedPreset, { preset });
    await ctxProps.dispatch(loadGraph, {
      overlay: 'none',
      parameters: item.parameters,
      transferPreviousState: false,
    });
  };

  return (
    <>
      <ConfirmDialog
        id={DELETE_CHECK_CONFIRM}
        msg={msg.canvasGraph.graphSidePanel.pathChecks.deleteConfirm}
        onClickConfirm={handleDeleteCheckConfirm}
        btnConfirmVariant='primary'
        btnConfirmAccent='negative'
        btnCancelVariant='secondary'
        btnCancelAccent='positive'
      />
      <div className='flex flex-1 flex-col'>
        <GraphPathChecksFilter pathChecksHook={props.pathChecksHook} />
        <GraphPathChecksList
          onDeleteCheck={handleDeleteCheck}
          onFetchGraph={handleFetchGraph}
          pathChecksHook={props.pathChecksHook}
        />
      </div>
    </>
  );
};

export const GraphPathChecks = memo(BareGraphPathChecks);
